import React from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../../../context";
import useFetchObjects from "../../../../api/useFetchObjects";

const CostSummary = ({ patientId }) => {
  const { t } = useTranslation();
  const auth = useAuth();
  const token = auth?.user?.token;

  // Fetch prescriptions for cost calculation
  const {
    data: prescriptionData,
    isLoading: presLoading,
    isError: presError,
  } = useFetchObjects(
    patientId ? ["prescriptions", patientId, "costs"] : null,
    patientId ? `prescriptions/?patient=${patientId}` : "",
    token
  );

  // Fetch IPD charges
  const {
    data: ipdData,
    isLoading: ipdLoading,
    isError: ipdError,
  } = useFetchObjects(
    patientId ? ["ipd-admissions", patientId] : null,
    patientId ? `ipd-admissions/?patient=${patientId}` : "",
    token
  );

  // Fetch ICU charges
  const {
    data: icuData,
    isLoading: icuLoading,
    isError: icuError,
  } = useFetchObjects(
    patientId ? ["icu-admissions", patientId] : null,
    patientId ? `icu-admissions/?patient=${patientId}` : "",
    token
  );

  const isLoading = presLoading || ipdLoading || icuLoading;
  const isError = presError || ipdError || icuError;

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (isError) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {t("profile.errorLoadingCosts") || "Error loading cost information"}
      </Alert>
    );
  }

  const prescriptions = prescriptionData?.results || [];
  const ipdAdmissions = ipdData?.results || [];
  const icuAdmissions = icuData?.results || [];

  // Calculate totals
  let totalPrescriptionCost = 0;
  let paidPrescriptionCost = 0;
  let pendingPrescriptionCost = 0;

  prescriptions.forEach((prescription) => {
    const cost = parseFloat(prescription.total_price) || 0;
    totalPrescriptionCost += cost;
    if (prescription.payment_status === 2) {
      // Paid
      paidPrescriptionCost += cost;
    } else {
      pendingPrescriptionCost += cost;
    }
  });

  let totalIPDCost = 0;
  let paidIPDCost = 0;
  ipdAdmissions.forEach((admission) => {
    const cost = parseFloat(admission.total_charges) || 0;
    totalIPDCost += cost;
    const paid = parseFloat(admission.total_paid) || 0;
    paidIPDCost += paid;
  });

  let totalICUCost = 0;
  let paidICUCost = 0;
  icuAdmissions.forEach((admission) => {
    const cost = parseFloat(admission.total_charges) || 0;
    totalICUCost += cost;
    const paid = parseFloat(admission.total_paid) || 0;
    paidICUCost += paid;
  });

  const grandTotal =
    totalPrescriptionCost + totalIPDCost + totalICUCost;
  const totalPaid = paidPrescriptionCost + paidIPDCost + paidICUCost;
  const totalPending = grandTotal - totalPaid;

  const costItems = [
    {
      category: t("profile.prescriptions") || "Prescriptions",
      total: totalPrescriptionCost,
      paid: paidPrescriptionCost,
      pending: pendingPrescriptionCost,
    },
    {
      category: t("profile.ipdCharges") || "IPD Charges",
      total: totalIPDCost,
      paid: paidIPDCost,
      pending: totalIPDCost - paidIPDCost,
    },
    {
      category: t("profile.icuCharges") || "ICU Charges",
      total: totalICUCost,
      paid: paidICUCost,
      pending: totalICUCost - paidICUCost,
    },
  ];

  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="h6" gutterBottom>
        {t("profile.costSummary") || "Cost Summary"}
      </Typography>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="body2" color="text.secondary">
                {t("profile.totalCharges") || "Total Charges"}
              </Typography>
              <Typography variant="h5" fontWeight="bold" color="primary">
                {grandTotal.toFixed(2)} {t("profile.currency") || "AFN"}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="body2" color="text.secondary">
                {t("profile.totalPaid") || "Total Paid"}
              </Typography>
              <Typography variant="h5" fontWeight="bold" color="success.main">
                {totalPaid.toFixed(2)} {t("profile.currency") || "AFN"}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="body2" color="text.secondary">
                {t("profile.totalPending") || "Total Pending"}
              </Typography>
              <Typography variant="h5" fontWeight="bold" color="warning.main">
                {totalPending.toFixed(2)} {t("profile.currency") || "AFN"}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <TableContainer component={Paper} variant="outlined">
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>
                <strong>{t("profile.category") || "Category"}</strong>
              </TableCell>
              <TableCell align="right">
                <strong>{t("profile.total") || "Total"}</strong>
              </TableCell>
              <TableCell align="right">
                <strong>{t("profile.paid") || "Paid"}</strong>
              </TableCell>
              <TableCell align="right">
                <strong>{t("profile.pending") || "Pending"}</strong>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {costItems.map((item) => (
              <TableRow key={item.category}>
                <TableCell>{item.category}</TableCell>
                <TableCell align="right">
                  {item.total.toFixed(2)} {t("profile.currency") || "AFN"}
                </TableCell>
                <TableCell align="right">
                  {item.paid.toFixed(2)} {t("profile.currency") || "AFN"}
                </TableCell>
                <TableCell align="right">
                  {item.pending.toFixed(2)} {t("profile.currency") || "AFN"}
                </TableCell>
              </TableRow>
            ))}
            <TableRow>
              <TableCell>
                <strong>{t("profile.grandTotal") || "Grand Total"}</strong>
              </TableCell>
              <TableCell align="right">
                <strong>
                  {grandTotal.toFixed(2)} {t("profile.currency") || "AFN"}
                </strong>
              </TableCell>
              <TableCell align="right">
                <strong>
                  {totalPaid.toFixed(2)} {t("profile.currency") || "AFN"}
                </strong>
              </TableCell>
              <TableCell align="right">
                <strong>
                  {totalPending.toFixed(2)} {t("profile.currency") || "AFN"}
                </strong>
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default CostSummary;
